import { useMemo, useState } from "react";
import { X, Store } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Field } from "@/components/Bits";

const input = "w-full rounded-sm border border-line bg-white px-4 py-2.5 text-sm outline-none transition-colors focus:border-lime";
const blank = {
  name: "", website: "", account_rep: "", email: "", phone: "", address: "",
  vendor_type: "", notes: "",
};
const TYPES = ["Retail", "Trade / To the Trade", "Manufacturer", "Artisan / Maker", "Antique / Vintage", "Workroom", "Other"];

/** Creates a vendor from inside the product form. Only the name is required. */
export default function NewVendorDialog({ open, onClose, onCreated, existing = [] }) {
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const host = (url) => {
    try {
      return new URL(url.startsWith("http") ? url : `https://${url}`).hostname.replace("www.", "").toLowerCase();
    } catch {
      return "";
    }
  };

  const duplicate = useMemo(() => {
    const name = form.name.trim().toLowerCase();
    const site = host(form.website.trim());
    return existing.find(
      (v) =>
        (name && (v.name || "").toLowerCase() === name) ||
        (site && host(v.website || "") === site)
    );
  }, [form.name, form.website, existing]);

  if (!open) return null;

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.name.trim()) {
      toast.error("A vendor name is required.");
      return;
    }
    const email = form.email.trim();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      toast.error("Enter a valid email address.");
      return;
    }
    setBusy(true);
    try {
      const site = form.website.trim();
      const { data } = await api.post("/vendors", {
        ...form,
        email: email || null,
        website: site ? (site.startsWith("http") ? site : `https://${site}`) : null,
        vendor_type: form.vendor_type || "Retail",
        status: "Active",
      });
      toast.success(`${data.name} added to your vendors`);
      onCreated(data);
      setForm(blank);
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 sm:items-center sm:p-6" data-testid="new-vendor-dialog">
      {/* A plain container, not a form: this dialog renders inside the product form. */}
      <div
        onKeyDown={(e) => { if (e.key === "Enter" && e.target.tagName !== "TEXTAREA") submit(e); }}
        className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-t-sm bg-white p-6 sm:rounded-sm sm:p-8"
      >
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <span className="overline">New vendor</span>
            <h3 className="mt-2 font-serif text-2xl text-ink">Add a vendor</h3>
            <p className="mt-1 text-xs text-fog">Saved to your vendor directory — editable there any time.</p>
          </div>
          <button
            type="button"
            data-testid="new-vendor-close"
            onClick={() => { setForm(blank); onClose(); }}
            className="rounded-sm border border-line p-1.5 text-stone transition-colors hover:border-fog"
            aria-label="Close"
          >
            <X className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>

        {duplicate && (
          <div className="mb-6 rounded-sm border border-line bg-bone px-4 py-3 text-sm text-stone" data-testid="vendor-duplicate-warning">
            <strong className="text-ink">{duplicate.name}</strong> is already in your directory.{" "}
            <button
              type="button"
              data-testid="vendor-use-existing"
              onClick={() => { onCreated(duplicate); setForm(blank); onClose(); }}
              className="text-lime-dark underline underline-offset-2"
            >
              Use that vendor instead
            </button>
          </div>
        )}

        <div className="grid gap-6 sm:grid-cols-2">
          <Field label="Vendor / Business Name *" className="sm:col-span-2">
            <input data-testid="vendor-name-input" value={form.name} onChange={set("name")} className={input} />
          </Field>
          <Field label="Website (optional)">
            <input data-testid="vendor-website-input" value={form.website} onChange={set("website")} placeholder="https://…" className={input} />
          </Field>
          <Field label="Category / Specialty (optional)">
            <select data-testid="vendor-type-select" value={form.vendor_type} onChange={set("vendor_type")} className={input}>
              <option value="">— Select —</option>
              {TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Contact Name (optional)">
            <input data-testid="vendor-contact-input" value={form.account_rep} onChange={set("account_rep")} className={input} />
          </Field>
          <Field label="Email (optional)">
            <input data-testid="vendor-email-input" type="email" value={form.email} onChange={set("email")} className={input} />
          </Field>
          <Field label="Phone (optional)">
            <input data-testid="vendor-phone-input" value={form.phone} onChange={set("phone")} className={input} />
          </Field>
          <Field label="Business Address (optional)">
            <input data-testid="vendor-address-input" value={form.address} onChange={set("address")} className={input} />
          </Field>
          <Field label="Notes (optional)" className="sm:col-span-2">
            <textarea data-testid="vendor-notes-input" rows={3} value={form.notes} onChange={set("notes")} className={input} />
          </Field>
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <button
            type="button"
            data-testid="vendor-save-btn"
            onClick={submit}
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-sm bg-lime px-5 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-lime-dark disabled:opacity-60"
          >
            <Store className="h-4 w-4" strokeWidth={1.5} /> {busy ? "Saving…" : "Save Vendor"}
          </button>
          <button
            type="button"
            onClick={() => { setForm(blank); onClose(); }}
            className="rounded-sm border border-line bg-white px-4 py-2.5 text-sm text-ink transition-colors hover:bg-line/50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
